import React from 'react';

import { NAVIGATION } from '@lumapps/navigation/keys';
import { NavigationMapItem } from '@lumapps/navigation/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { LinearProgressSuspenseFallback } from '@lumapps/utils/components/LinearProgressSuspenseFallback';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { mainNavigationAdminConfiguration } from '../MainNavigationAdminPage/MainNavigationAdminPage';
import {
    NavigationMapContextProvider,
    NavigationMapContextProviderProps,
} from '../NavigationMapContextProvider/NavigationMapContextProvider';
import { SetNodeDestinationDialog, SetNodeDestinationDialogProps } from '../SetNodeDestinationDialog';

export interface AddToMainNavigationDialogProps {
    /** The node to add to the main navigation */
    node?: NavigationMapItem | null;
    /** Dictionary of Groups id and their names, so we can display the list of visibleBy per node without extra call */
    visibleByGroupReferences: NavigationMapContextProviderProps['visibleByGroupReferences'];
    /** The current loading state */
    loadingState?: BaseLoadingStatus;
    /** The navigation nodes to display */
    nodes?: Array<NavigationMapItem>;
    /** Whether the dialog should be opened */
    isOpen: boolean;
    /** Callback on dialog close */
    onClose(): void;
    /** The available languages, for node editing */
    languages: Array<string>;
    /** The default language to use */
    defaultLanguage: string;
    /** Callback on dialog save */
    onSave: SetNodeDestinationDialogProps['onSave'];
    /** Show the dialog as loading */
    dialogIsLoading?: boolean;
    /** Callback to manage the save disable status and get an error message */
    isSaveDisabled: SetNodeDestinationDialogProps['isSaveDisabled'];
}

export const AddToMainNavigationDialog: React.FC<AddToMainNavigationDialogProps> = ({
    node,
    visibleByGroupReferences,
    loadingState,
    nodes,
    isOpen,
    onClose,
    languages,
    defaultLanguage,
    dialogIsLoading,
    onSave,
    isSaveDisabled,
}) => {
    const { translateKey } = useTranslate();

    if (loadingState === BaseLoadingStatus.loading) {
        return <LinearProgressSuspenseFallback />;
    }

    if (!node || !nodes) {
        return null;
    }

    return (
        <NavigationMapContextProvider
            configuration={mainNavigationAdminConfiguration}
            visibleByGroupReferences={visibleByGroupReferences}
        >
            <SetNodeDestinationDialog
                isOpen={isOpen}
                onClose={onClose}
                nodes={nodes}
                nodeToMove={node}
                onSave={onSave}
                isListNode={() => true}
                scope="add-to-navigation"
                languages={languages}
                defaultLanguage={defaultLanguage}
                dialogTitle={translateKey(NAVIGATION.ADD_TO_NAVIGATION)}
                saveButtonTitle={({ mode }) =>
                    mode === 'destination' ? translateKey(GLOBAL.ADD_HERE) : translateKey(GLOBAL.DONE)
                }
                isSaveDisabled={isSaveDisabled}
                isLoading={dialogIsLoading}
            />
        </NavigationMapContextProvider>
    );
};

export default AddToMainNavigationDialog;
